import { Box, Grid, Typography } from '@mui/material';
import { format } from 'date-fns';
import { Fragment } from 'react';
import PrintModal from '../../components/PrintModal';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useLocale } from '../../contexts/locale.context';
import currency from '../../utils/currency';
import { ReportItem } from './ReportBySales';

interface Props {
  onClose: () => void;
  timeRange: [number, number];
  reportItems: ReportItem[];
  totals: ReportItem[];
  section: string;
  workerName: string;
  tableName: string;
  orderType: string;
}

const ReportBySalesPrintModal = (props: Props) => {
  const { onClose, timeRange, reportItems, totals, section, workerName, tableName, orderType } = props;
  const { cafe } = useAdmin();
  const { lang } = useLocale();
  const fontWeight = cafe?.bill?.boldText ? 800 : 400;

  return (
    <PrintModal onClose={onClose} isPosPrinter>
      <Box p={2} pb={5} color="#000000">
        <Typography variant="h6" sx={{ mt: 1, mb: 1, textAlign: 'center', fontWeight }}>
          {t.sales[lang]}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight }}>{`${t.date[lang]}: ${format(
          timeRange[0],
          'dd.MM.yyyy HH:mm'
        )} - ${format(timeRange[1], 'dd.MM.yyyy HH:mm')}`}</Typography>

        {section && (
          <Typography variant="subtitle1" sx={{ fontWeight }}>{`${t.menu_section[lang]}: ${section}`}</Typography>
        )}
        {workerName && (
          <Typography variant="subtitle1" sx={{ fontWeight }}>{`${t.waiter[lang]}: ${workerName}`}</Typography>
        )}
        {tableName && (
          <Typography variant="subtitle1" sx={{ fontWeight }}>{`${t.table[lang]}: ${tableName}`}</Typography>
        )}
        {orderType && (
          <Typography variant="subtitle1" sx={{ fontWeight }}>{`${t.order_type[lang]}: ${orderType}`}</Typography>
        )}

        <Grid container spacing={0.5} sx={{ borderBottom: '1px solid #555', mb: 1, mt: 2 }}>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight }}>
              {t.name[lang]}
            </Typography>
          </Grid>
          <Grid item xs={3} textAlign="right">
            <Typography variant="body2" sx={{ fontWeight, whiteSpace: 'nowrap' }}>
              {t.qty[lang]}
            </Typography>
          </Grid>
          <Grid item xs={3} textAlign="right">
            <Typography variant="body2" sx={{ fontWeight }}>
              {t.total[lang]}
            </Typography>
          </Grid>
        </Grid>

        {reportItems.map((i) => (
          <Grid key={i.id} container spacing={0.5} sx={{ lineHeight: 1.1, mb: 1 }}>
            <Grid item xs={6}>
              <Typography sx={{ lineHeight: 1.2, fontWeight }}>{i.dishName}</Typography>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography sx={{ lineHeight: 1.2, fontWeight }}>
                {`${i.qnt ? i.qnt : ''}${i.qnt && i.weight ? ' + ' : ''}${
                  i.weight ? `${i.weight}${t[i.unit][lang]}` : ''
                }`}
              </Typography>
            </Grid>
            <Grid item xs={3} textAlign="right">
              <Typography sx={{ lineHeight: 1.2, fontWeight }}>{currency(i.total + i.service - i.discount)}</Typography>
            </Grid>
          </Grid>
        ))}

        <Grid container spacing={0.5} sx={{ borderTop: '1px solid #555', mt: 1 }}>
          {totals.map((total) => (
            <Fragment key={total.id}>
              <Grid item xs={8}>
                <Typography variant="h6" sx={{ fontWeight }}>
                  {total.dishName}
                </Typography>
              </Grid>
              <Grid item xs={4} textAlign="right">
                <Typography variant="h6" sx={{ fontWeight }}>
                  {currency(total.total + total.service - total.discount)}
                </Typography>
              </Grid>
            </Fragment>
          ))}
        </Grid>
      </Box>
    </PrintModal>
  );
};

export default ReportBySalesPrintModal;
